import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Tag from "antd/es/tag";
import { useIntl } from "react-intl";

import Organization from "@mapmycustomers/shared/types/Organization";
import Sandbox from "@mapmycustomers/shared/types/Sandbox";
import { Button } from "@mapmycustomers/ui";

import configService from "@app/config/ConfigService";
import { fetchSandboxAccessToken } from "@app/store/app/actions";
import { getOrganization } from "@app/store/iam";
import { selectService } from "@app/store/integrations/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { getAppUrl } from "@app/util/appUrl";

import styles from "./SandboxIntegrationReAuth.module.scss";

interface Props {
  fetchSandboxAccessToken: typeof fetchSandboxAccessToken.request;
  organization?: Organization;
  selectService: typeof selectService;
  showActions: boolean;
}

const SandboxIntegrationReAuth: React.FC<Props> = ({
  fetchSandboxAccessToken,
  organization,
  selectService,
  showActions,
}) => {
  const intl = useIntl();
  const currentSandbox: Sandbox | undefined = organization?.metaData?.connectedOrg;

  const handleSwitchToMainApp = useCallback(() => {
    fetchSandboxAccessToken({
      callback: (token) => {
        window.location.href = getAppUrl(
          configService.getBaseAppUrl(),
          token,
          "/settings/organization/sandbox"
        );
      },
    });
  }, [fetchSandboxAccessToken]);

  const handleReAuth = useCallback(() => {
    if (currentSandbox?.crm) {
      selectService({ integrationService: currentSandbox.crm, isSandbox: false });
    }
  }, [currentSandbox?.crm, selectService]);

  return (
    <div className={styles.container}>
      <Row align="middle" gutter={layout.spacerM}>
        <Col>
          <FontAwesomeIcon className={styles.icon} icon={faInfoCircle} size="lg" />
        </Col>
        <Col>{currentSandbox?.name}</Col>
        {showActions && (
          <Col>
            <Tag color="warning">
              {intl.formatMessage({
                id: "settings.component.sandboxIntegrationRequirement.tag",
                defaultMessage: "Setup still required",
                description: "Settings Sandbox Integration - setup required",
              })}
            </Tag>
          </Col>
        )}
        {showActions && (
          <Col flex={1}>
            <Row justify="end">
              <Col>
                <Button onClick={handleSwitchToMainApp} size="small">
                  {intl.formatMessage({
                    id: "settings.component.sandboxIntegrationRequirement.backToProd",
                    defaultMessage: "Back to main account",
                    description: "Settings Sandbox Environment - Back to main account",
                  })}
                </Button>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
      <div>
        {intl.formatMessage(
          {
            id: "settings.component.sandboxIntegrationRequirement.description",
            defaultMessage:
              "Before your integration configuration can be cloned into your Sandbox environment, <b>you must re-authenticate with your CRM.</b>",
            description: "Settings Sandbox Environment - description",
          },
          { b: (text) => <b>{text}</b> }
        )}
      </div>
      <div>
        <Button onClick={handleReAuth} type="primary">
          {intl.formatMessage({
            id: "settings.component.sandboxIntegrationRequirement.authenticate",
            defaultMessage: "Authenticate",
            description: "Settings Sandbox Environment - Authenticate",
          })}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  organization: getOrganization(state),
});

const mapDispatchToProps = {
  fetchSandboxAccessToken: fetchSandboxAccessToken.request,
  selectService,
};

export default connect(mapStateToProps, mapDispatchToProps)(SandboxIntegrationReAuth);
